import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api, { commissionAPI, paymentAPI } from '../services/api';

function AdminDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalWorkers: 0,
    totalBookings: 0,
    pendingWorkers: 0,
    completedBookings: 0,
    revenue: 0
  });
  const [users, setUsers] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [commissionData, setCommissionData] = useState(null);
  const [pendingPayouts, setPendingPayouts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
    fetchAllData();
  }, []);

  const checkAuth = () => {
    const token = localStorage.getItem('token');
    const userType = localStorage.getItem('userType');
    if (!token || userType !== 'admin') {
      toast.error('Please login as admin');
      navigate('/admin/login');
    }
  };

  const fetchAllData = async () => {
    try {
      setLoading(true);
      
      // Fetch stats
      const statsRes = await api.get('/admin/stats');
      setStats(statsRes.data.data);

      // Fetch users
      const usersRes = await api.get('/admin/users');
      setUsers(usersRes.data.data);

      // Fetch workers
      const workersRes = await api.get('/admin/workers');
      setWorkers(workersRes.data.data);

      // Fetch bookings
      const bookingsRes = await api.get('/admin/bookings');
      setBookings(bookingsRes.data.data);

      // Fetch categories
      const categoriesRes = await api.get('/categories');
      setCategories(categoriesRes.data.data);

      // Fetch commission data
      const commissionRes = await commissionAPI.getAllCommissions();
      setCommissionData(commissionRes.data.data);

      // Fetch pending payouts
      const payoutsRes = await paymentAPI.getPendingPayouts();
      setPendingPayouts(payoutsRes.data.data);

    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyWorker = async (workerId) => {
    try {
      await api.put(`/admin/workers/${workerId}/verify`, { status: 'approved' });
      toast.success('Worker verified successfully! ✅');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to verify worker');
    }
  };

  const handleRejectWorker = async (workerId) => {
    const reason = prompt('Reason for rejection:');
    if (!reason) return;

    try {
      await api.put(`/admin/workers/${workerId}/reject`, { reason });
      toast.success('Worker rejected');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to reject worker');
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    try {
      await api.put(`/admin/users/${userId}/toggle-status`, { isActive: !currentStatus });
      toast.success(currentStatus ? 'User deactivated' : 'User activated');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const handleToggleWorkerStatus = async (workerId, currentStatus) => {
    try {
      await api.put(`/admin/workers/${workerId}/toggle-status`, { isActive: !currentStatus });
      toast.success(currentStatus ? 'Worker deactivated' : 'Worker activated');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update worker status');
    }
  };

  const handleMarkPaid = async (bookingId, workerName, amount) => {
    const payoutMethod = prompt('Payment method (upi/bank_transfer):') || 'upi';
    const payoutReference = prompt('Enter UPI transaction ID or reference:');
    
    if (!payoutReference) {
      toast.error('Transaction reference is required');
      return;
    }

    try {
      await paymentAPI.markWorkerPaid(bookingId, {
        payoutMethod,
        payoutReference
      });
      toast.success(`✅ Payment marked as paid for ${workerName} - ₹${amount}`);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to mark payment as paid');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userType');
    localStorage.removeItem('userName');
    toast.success('Logged out successfully!');
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      
      {/* Admin Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                <span className="text-2xl">👨‍💼</span>
              </div>
              <div>
                <h1 className="text-2xl font-bold">Admin Panel</h1>
                <p className="text-sm opacity-90">ServiceWala Management</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="px-6 py-2 bg-white text-purple-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              🚪 Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          
          <div className="bg-gradient-to-br from-blue-400 to-blue-600 rounded-2xl shadow-lg p-6 text-white">
            <div className="flex items-center justify-between mb-2">
              <span className="text-3xl">👥</span>
              <span className="text-sm opacity-80">Users</span>
            </div>
            <div className="text-4xl font-bold mb-1">{stats.totalUsers}</div>
            <div className="text-sm opacity-90">Total Customers</div>
          </div>

          <div className="bg-gradient-to-br from-green-400 to-green-600 rounded-2xl shadow-lg p-6 text-white">
            <div className="flex items-center justify-between mb-2">
              <span className="text-3xl">👨‍🔧</span>
              <span className="text-sm opacity-80">Workers</span>
            </div>
            <div className="text-4xl font-bold mb-1">{stats.totalWorkers}</div>
            <div className="text-sm opacity-90">
              <span className="bg-yellow-500 px-2 py-1 rounded text-xs">
                {stats.pendingWorkers} Pending
              </span>
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-400 to-purple-600 rounded-2xl shadow-lg p-6 text-white">
            <div className="flex items-center justify-between mb-2">
              <span className="text-3xl">📋</span>
              <span className="text-sm opacity-80">Bookings</span>
            </div>
            <div className="text-4xl font-bold mb-1">{stats.totalBookings}</div>
            <div className="text-sm opacity-90">{stats.completedBookings} Completed</div>
          </div>

          <div className="bg-gradient-to-br from-orange-400 to-red-600 rounded-2xl shadow-lg p-6 text-white">
            <div className="flex items-center justify-between mb-2">
              <span className="text-3xl">💰</span>
              <span className="text-sm opacity-80">Revenue</span>
            </div>
            <div className="text-4xl font-bold mb-1">₹{stats.revenue}</div>
            <div className="text-sm opacity-90">Total Earnings</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          
          {/* Tab Headers */}
          <div className="border-b border-gray-200">
            <div className="flex overflow-x-auto">
              <button
                onClick={() => setActiveTab('overview')}
                className={`px-6 py-4 font-semibold transition-colors min-w-[150px] ${
                  activeTab === 'overview'
                    ? 'bg-purple-600 text-white'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                📊 Overview
              </button>
              <button
                onClick={() => setActiveTab('workers')}
                className={`px-6 py-4 font-semibold transition-colors min-w-[150px] ${
                  activeTab === 'workers'
                    ? 'bg-purple-600 text-white'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                👨‍🔧 Workers ({workers.length})
              </button>
              <button
                onClick={() => setActiveTab('users')}
                className={`px-6 py-4 font-semibold transition-colors min-w-[150px] ${
                  activeTab === 'users'
                    ? 'bg-purple-600 text-white'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                👥 Users ({users.length})
              </button>
              <button
                onClick={() => setActiveTab('bookings')}
                className={`px-6 py-4 font-semibold transition-colors min-w-[150px] ${
                  activeTab === 'bookings'
                    ? 'bg-purple-600 text-white'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                📋 Bookings ({bookings.length})
              </button>
              <button
                onClick={() => setActiveTab('categories')}
                className={`px-6 py-4 font-semibold transition-colors min-w-[150px] ${
                  activeTab === 'categories'
                    ? 'bg-purple-600 text-white'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                🏷️ Categories ({categories.length})
              </button>
              <button
                onClick={() => setActiveTab('commission')}
                className={`px-6 py-4 font-semibold transition-colors min-w-[150px] ${
                  activeTab === 'commission'
                    ? 'bg-purple-600 text-white'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                💰 Commission
              </button>
              <button
                onClick={() => setActiveTab('payouts')}
                className={`px-6 py-4 font-semibold transition-colors min-w-[150px] ${
                  activeTab === 'payouts'
                    ? 'bg-purple-600 text-white'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                💸 Pending Payouts
                {pendingPayouts.length > 0 && (
                  <span className="ml-2 px-2 py-1 bg-red-500 text-white rounded-full text-xs">
                    {pendingPayouts.length}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-6">Platform Overview</h2>
                
                <div className="grid md:grid-cols-2 gap-6">
                  
                  {/* Recent Activity */}
                  <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-6">
                    <h3 className="text-xl font-bold text-gray-800 mb-4">📈 Quick Stats</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-700">Active Users:</span>
                        <span className="font-bold">{users.filter(u => u.isActive).length}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-700">Verified Workers:</span>
                        <span className="font-bold">
                          {workers.filter(w => w.verification?.isVerified).length}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-700">Pending Approvals:</span>
                        <span className="font-bold text-yellow-600">
                          {workers.filter(w => w.verification?.status === 'pending').length}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-700">Today's Bookings:</span>
                        <span className="font-bold text-green-600">
                          {bookings.filter(b => 
                            new Date(b.createdAt).toDateString() === new Date().toDateString()
                          ).length}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Platform Health */}
                  <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-xl p-6">
                    <h3 className="text-xl font-bold text-gray-800 mb-4">💚 Platform Health</h3>
                    <div className="space-y-4">
                      <div>
                        <div className="flex justify-between mb-1">
                          <span className="text-sm text-gray-700">Worker Approval Rate</span>
                          <span className="text-sm font-bold">
                            {workers.length > 0 
                              ? Math.round((workers.filter(w => w.verification?.isVerified).length / workers.length) * 100)
                              : 0}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-green-500 h-2 rounded-full" 
                            style={{ 
                              width: `${workers.length > 0 
                                ? (workers.filter(w => w.verification?.isVerified).length / workers.length) * 100
                                : 0}%` 
                            }}
                          ></div>
                        </div>
                      </div>
                      
                      <div>
                        <div className="flex justify-between mb-1">
                          <span className="text-sm text-gray-700">Booking Completion Rate</span>
                          <span className="text-sm font-bold">
                            {bookings.length > 0 
                              ? Math.round((stats.completedBookings / bookings.length) * 100)
                              : 0}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-purple-500 h-2 rounded-full" 
                            style={{ 
                              width: `${bookings.length > 0 
                                ? (stats.completedBookings / bookings.length) * 100
                                : 0}%` 
                            }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Pending Workers Alert */}
                {workers.filter(w => w.verification?.status === 'pending').length > 0 && (
                  <div className="mt-6 bg-yellow-50 border-l-4 border-yellow-500 p-6 rounded-lg">
                    <div className="flex items-center">
                      <span className="text-3xl mr-4">⚠️</span>
                      <div className="flex-1">
                        <h4 className="text-lg font-bold text-yellow-800 mb-1">
                          Pending Worker Approvals
                        </h4>
                        <p className="text-yellow-700">
                          You have {workers.filter(w => w.verification?.status === 'pending').length} workers 
                          waiting for verification. Click on "Workers" tab to review.
                        </p>
                      </div>
                      <button
                        onClick={() => setActiveTab('workers')}
                        className="px-6 py-2 bg-yellow-500 text-white rounded-lg font-semibold hover:bg-yellow-600"
                      >
                        Review Now
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Workers Tab */}
            {activeTab === 'workers' && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-800">Workers Management</h2>
                  <div className="flex space-x-2">
                    <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                      {workers.filter(w => w.verification?.isVerified).length} Verified
                    </span>
                    <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
                      {workers.filter(w => w.verification?.status === 'pending').length} Pending
                    </span>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Worker</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Experience</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rate</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {workers.map((worker) => (
                        <tr key={worker._id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="font-medium text-gray-900">{worker.name}</div>
                              <div className="text-sm text-gray-500">{worker.phone}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm text-gray-900">
                              {worker.category?.icon} {worker.category?.name}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm text-gray-900">{worker.experience} years</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm text-gray-900">₹{worker.hourlyRate}/hr</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {worker.verification?.isVerified ? (
                              <span className="px-3 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                                ✅ Verified
                              </span>
                            ) : worker.verification?.status === 'rejected' ? (
                              <span className="px-3 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
                                ❌ Rejected
                              </span>
                            ) : (
                              <span className="px-3 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
                                ⏳ Pending
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {worker.verification?.status === 'pending' ? (
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => handleVerifyWorker(worker._id)}
                                  className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600"
                                >
                                  ✅ Approve
                                </button>
                                <button
                                  onClick={() => handleRejectWorker(worker._id)}
                                  className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                                >
                                  ❌ Reject
                                </button>
                              </div>
                            ) : worker.verification?.isVerified ? (
                              <button
                                onClick={() => handleToggleWorkerStatus(worker._id, worker.isActive)}
                                className={`px-3 py-1 rounded ${
                                  worker.isActive
                                    ? 'bg-red-500 text-white hover:bg-red-600'
                                    : 'bg-green-500 text-white hover:bg-green-600'
                                }`}
                              >
                                {worker.isActive ? '🚫 Deactivate' : '✅ Activate'}
                              </button>
                            ) : null}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Users Tab */}
            {activeTab === 'users' && (
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-6">Users Management</h2>
                
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Joined</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {users.map((user) => (
                        <tr key={user._id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="font-medium text-gray-900">{user.name}</div>
                              <div className="text-sm text-gray-500">{user.phone}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm text-gray-900">{user.email || 'N/A'}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm text-gray-900">{user.location?.city || 'N/A'}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                              user.isActive 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {user.isActive ? '🟢 Active' : '🔴 Inactive'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(user.createdAt).toLocaleDateString('en-IN')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <button
                              onClick={() => handleToggleUserStatus(user._id, user.isActive)}
                              className={`px-3 py-1 rounded ${
                                user.isActive
                                  ? 'bg-red-500 text-white hover:bg-red-600'
                                  : 'bg-green-500 text-white hover:bg-green-600'
                              }`}
                            >
                              {user.isActive ? '🚫 Deactivate' : '✅ Activate'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Bookings Tab */}
            {activeTab === 'bookings' && (
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-6">All Bookings</h2>
                
                <div className="space-y-4">
                  {bookings.map((booking) => (
                    <div key={booking._id} className="border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className="text-lg font-bold text-gray-800">
                              {booking.category?.icon} {booking.category?.name}
                            </h3>
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              booking.status === 'completed' ? 'bg-green-100 text-green-800' :
                              booking.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              booking.status === 'accepted' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {booking.status}
                            </span>
                          </div>
                          <div className="grid md:grid-cols-2 gap-2 text-sm text-gray-600">
                            <p>👤 Customer: {booking.customer?.name}</p>
                            <p>👨‍🔧 Worker: {booking.worker?.name}</p>
                            <p>📅 Date: {new Date(booking.scheduledDate).toLocaleDateString('en-IN')}</p>
                            <p>💰 Amount: ₹{booking.pricing?.finalAmount || 'Pending'}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Categories Tab */}
            {activeTab === 'categories' && (
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-6">Service Categories</h2>
                
                <div className="grid md:grid-cols-3 gap-4">
                  {categories.map((category) => (
                    <div key={category._id} className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-6 border-2 border-blue-200">
                      <div className="text-4xl mb-2">{category.icon}</div>
                      <h3 className="text-xl font-bold text-gray-800 mb-1">{category.name}</h3>
                      <p className="text-sm text-gray-600">{category.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Commission Tab */}
            {activeTab === 'commission' && (
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-6">Commission Management</h2>

                {!commissionData?.commissionEnabled ? (
                  <div className="text-center py-12 bg-yellow-50 rounded-xl">
                    <div className="text-6xl mb-4">⚠️</div>
                    <h3 className="text-2xl font-bold text-yellow-800 mb-2">Commission Disabled</h3>
                    <p className="text-yellow-700">Commission system is currently OFF.</p>
                    <p className="text-yellow-600 text-sm mt-2">Render me COMMISSION_ENABLED=true karo to enable karne ke liye.</p>
                  </div>
                ) : (
                  <div className="space-y-6">

                    {/* Summary Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-red-50 rounded-xl p-6 text-center">
                        <p className="text-gray-600 text-sm mb-1">Total Pending</p>
                        <p className="text-3xl font-bold text-red-600">
                          ₹{commissionData?.summary?.totalPending || 0}
                        </p>
                      </div>
                      <div className="bg-green-50 rounded-xl p-6 text-center">
                        <p className="text-gray-600 text-sm mb-1">Total Collected</p>
                        <p className="text-3xl font-bold text-green-600">
                          ₹{commissionData?.summary?.totalCollected || 0}
                        </p>
                      </div>
                      <div className="bg-blue-50 rounded-xl p-6 text-center">
                        <p className="text-gray-600 text-sm mb-1">Commission Rate</p>
                        <p className="text-3xl font-bold text-blue-600">
                          {commissionData?.commissionRate || 0}%
                        </p>
                      </div>
                    </div>

                    {/* Workers With Pending */}
                    {commissionData?.workersWithPending?.length > 0 && (
                      <div>
                        <h3 className="text-xl font-bold text-gray-800 mb-4">⏳ Workers With Pending Commission</h3>
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Worker</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pending Amount</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pending Count</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {commissionData.workersWithPending.map((worker) => (
                                <tr key={worker._id} className="hover:bg-gray-50">
                                  <td className="px-6 py-4">
                                    <div>
                                      <p className="font-medium text-gray-900">{worker.name}</p>
                                      <p className="text-sm text-gray-500">{worker.phone}</p>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4">
                                    <span className="font-bold text-red-600">
                                      ₹{worker.commission?.totalPending || 0}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4">
                                    <span className="font-bold">
                                      {worker.commission?.pendingCount || 0}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4">
                                    {worker.commission?.isBlocked ? (
                                      <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">
                                        🚫 Blocked
                                      </span>
                                    ) : (
                                      <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">
                                        ⏳ Pending
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Recent Transactions */}
                    {commissionData?.recentTransactions?.length > 0 && (
                      <div>
                        <h3 className="text-xl font-bold text-gray-800 mb-4">📋 Recent Transactions</h3>
                        <div className="space-y-3">
                          {commissionData.recentTransactions.map((transaction) => (
                            <div key={transaction._id} className="border border-gray-200 rounded-xl p-4">
                              <div className="flex justify-between items-center">
                                <div>
                                  <p className="font-bold text-gray-800">
                                    Worker: {transaction.worker?.name}
                                  </p>
                                  <p className="text-sm text-gray-600">
                                    Customer: {transaction.customer?.name}
                                  </p>
                                  <p className="text-sm text-gray-600">
                                    Method: {transaction.method} |
                                    Amount: ₹{transaction.amount}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className="font-bold text-purple-600">
                                    Commission: ₹{transaction.commissionAmount}
                                  </p>
                                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                    transaction.commissionStatus === 'collected'
                                      ? 'bg-green-100 text-green-800'
                                      : transaction.commissionStatus === 'pending'
                                      ? 'bg-yellow-100 text-yellow-800'
                                      : 'bg-gray-100 text-gray-800'
                                  }`}>
                                    {transaction.commissionStatus}
                                  </span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* No Data */}
                    {commissionData?.workersWithPending?.length === 0 && (
                      <div className="text-center py-8 bg-green-50 rounded-xl">
                        <div className="text-5xl mb-3">✅</div>
                        <h3 className="text-xl font-bold text-green-800">All Clear!</h3>
                        <p className="text-green-600">No pending commissions from any worker</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Pending Payouts Tab */}
            {activeTab === 'payouts' && (
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-6">💸 Pending Worker Payouts</h2>

                {pendingPayouts.length === 0 ? (
                  <div className="text-center py-12 bg-green-50 rounded-xl">
                    <div className="text-6xl mb-4">✅</div>
                    <h3 className="text-2xl font-bold text-green-800 mb-2">All Payments Complete!</h3>
                    <p className="text-green-700">No pending payouts to workers</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    
                    {/* Summary */}
                    <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-xl p-6 border-2 border-orange-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-xl font-bold text-gray-800 mb-1">
                            Total Pending Payouts
                          </h3>
                          <p className="text-gray-600">
                            {pendingPayouts.length} workers waiting for payment
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-4xl font-bold text-red-600">
                            ₹{pendingPayouts.reduce((sum, p) => sum + p.totalPending, 0)}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Workers List */}
                    {pendingPayouts.map((payout) => (
                      <div key={payout.workerId} className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                        
                        {/* Worker Header */}
                        <div className="flex items-start justify-between mb-4 pb-4 border-b border-gray-200">
                          <div>
                            <h3 className="text-xl font-bold text-gray-800">{payout.workerName}</h3>
                            <p className="text-sm text-gray-600">{payout.workerPhone}</p>
                            {payout.lastPayoutDate && (
                              <p className="text-xs text-gray-500 mt-1">
                                Last payout: {new Date(payout.lastPayoutDate).toLocaleDateString('en-IN')}
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-gray-600 mb-1">Total Pending</p>
                            <p className="text-3xl font-bold text-red-600">
                              ₹{payout.totalPending}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              {payout.pendingBookings.length} bookings
                            </p>
                          </div>
                        </div>

                        {/* Bookings List */}
                        <div className="space-y-2 mb-4">
                          <h4 className="font-semibold text-gray-700 text-sm mb-2">Pending Bookings:</h4>
                          {payout.pendingBookings.map((booking) => (
                            <div key={booking.bookingId} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                              <div>
                                <p className="font-medium text-gray-800">
                                  Booking #{booking.bookingId}
                                </p>
                                <p className="text-xs text-gray-500">
                                  Paid on: {new Date(booking.paidAt).toLocaleDateString('en-IN')}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="font-bold text-gray-800">₹{booking.amount}</p>
                                <button
                                  onClick={() => handleMarkPaid(booking.bookingId, payout.workerName, booking.amount)}
                                  className="mt-1 px-3 py-1 bg-green-500 text-white rounded-lg text-xs font-semibold hover:bg-green-600 transition-colors"
                                >
                                  ✅ Mark Paid
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Instructions */}
                        <div className="pt-4 border-t border-gray-200">
                          <p className="text-sm text-gray-600 mb-3">
                            💡 <strong>Instructions:</strong> Send ₹{payout.totalPending} via UPI/Bank to worker, 
                            then mark each booking as paid above.
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;